import React, { useEffect, useState } from "react";
import { translateUi } from "./i18n/index";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { CheckCircle2, FileCheck, Lock, X } from "./icons";
import { GbrModalBackdrop } from "./GbrModalBackdrop";
import { getAddonCatalogEntry } from "./addonCatalog";
import { useUiLocale } from "./useUiLocale";
import {
  DASHBOARD_GLASS_TILE,
  DASHBOARD_GRADIENT_PANE_SHELL,
  DashboardGradientPaneGlows,
} from "./dashboardGradientPane";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type ReportingQaComplianceData = {
  rate: number;
  employeeCount: number;
  checkCount: number;
  source: "stored" | "live";
  counts?: { C: number; A: number; R: number; NA: number };
};

type SeverityKey = "C" | "A" | "R" | "NA";

const SEVERITY_META: Record<
  SeverityKey,
  { label: string; dot: string; bar: string; value: string }
> = {
  C: {
    label: "Compliant",
    dot: "bg-emerald-400",
    bar: "from-emerald-400 to-emerald-500",
    value: "text-emerald-200",
  },
  A: {
    label: "Advisory",
    dot: "bg-amber-400",
    bar: "from-amber-400 to-amber-500",
    value: "text-amber-200",
  },
  R: {
    label: "Review",
    dot: "bg-rose-400",
    bar: "from-rose-400 to-rose-500",
    value: "text-rose-200",
  },
  NA: {
    label: "Not applicable",
    dot: "bg-slate-400",
    bar: "from-slate-400 to-slate-500",
    value: "text-slate-200",
  },
};

function AnimatedRate({
  target,
  active,
  className,
}: {
  target: number;
  active: boolean;
  className?: string;
}) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!active) {
      setDisplay(0);
      return;
    }
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setDisplay(target);
      return;
    }
    const durationMs = 950;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * target));
      if (progress < 1) frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [active, target]);

  return (
    <span className={cn("rv-reporting-rate-pop tabular-nums", className)} aria-hidden="true">
      {display}%
    </span>
  );
}

function BreakdownBody({
  data,
  animate,
}: {
  data: ReportingQaComplianceData;
  animate: boolean;
}) {
  const counts = data.counts ?? { C: 0, A: 0, R: 0, NA: 0 };
  const total = Math.max(1, data.checkCount || counts.C + counts.A + counts.R + counts.NA);
  const rows: SeverityKey[] = ["C", "A", "R", "NA"];
  const sourceLabel =
    data.source === "stored"
      ? translateUi("Latest saved verification per employee")
      : translateUi("Live verification scan");

  return (
    <div className="grid grid-cols-1 gap-3 bg-white p-3 sm:gap-4 sm:p-4 md:grid-cols-2">
      <div className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "flex min-h-[min(44vh,360px)] flex-col p-4 sm:p-5")}>
        <DashboardGradientPaneGlows />
        <div className="relative flex min-h-0 flex-1 flex-col gap-3">
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-200/90">
              {translateUi("Overall compliance")}
            </p>
            <AnimatedRate
              target={data.rate}
              active={animate}
              className="mt-1 block text-5xl font-bold tracking-tight text-white sm:text-6xl"
            />
            <p className="sr-only">{translateUi("{{rate}}% QA compliance rate", { rate: data.rate })}</p>
            <p className="mx-auto mt-2 max-w-xs text-xs leading-snug text-slate-300">
              {translateUi("Compliant checks ÷ total checks across your latest QA verification data.")}
            </p>
          </div>

          <div className={cn(DASHBOARD_GLASS_TILE, "p-3")}>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-300">
              {translateUi("How this is calculated")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-1.5 text-xs font-semibold sm:text-sm">
              <span className="rounded-md bg-emerald-500/20 px-2.5 py-1 text-emerald-100 ring-1 ring-emerald-400/25">
                {translateUi("Compliant {{count}}", { count: counts.C })}
              </span>
              <span className="text-slate-400">÷</span>
              <span className="rounded-md bg-white/10 px-2.5 py-1 text-white ring-1 ring-white/15">
                {translateUi("Total {{count}}", { count: data.checkCount })}
              </span>
              <span className="text-slate-400">× 100</span>
            </div>
          </div>

          <div className="mt-auto grid grid-cols-2 gap-2">
            <div className={cn(DASHBOARD_GLASS_TILE, "p-3")}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {translateUi("Employees included")}
              </p>
              <p className="mt-0.5 text-xl font-bold tabular-nums text-white">{data.employeeCount}</p>
            </div>
            <div className={cn(DASHBOARD_GLASS_TILE, "p-3")}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {translateUi("Data source")}
              </p>
              <p className="mt-0.5 text-xs font-semibold leading-snug text-white">{sourceLabel}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={cn(DASHBOARD_GRADIENT_PANE_SHELL, "flex min-h-[min(44vh,360px)] flex-col p-4 sm:p-5")}>
        <DashboardGradientPaneGlows />
        <div className="relative flex min-h-0 flex-1 flex-col">
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-200/90">
            {translateUi("Check breakdown")}
          </p>
          <div className="flex min-h-0 flex-1 flex-col justify-center gap-3">
            {rows.map((key, index) => {
              const count = counts[key];
              const pct = (count / total) * 100;
              const meta = SEVERITY_META[key];
              return (
                <div key={key} className={cn(DASHBOARD_GLASS_TILE, "space-y-1.5 p-2.5 sm:p-3")}>
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", meta.dot)} aria-hidden />
                      <span className="truncate font-semibold text-white">{translateUi(meta.label)}</span>
                    </div>
                    <span className={cn("shrink-0 tabular-nums text-xs font-bold sm:text-sm", meta.value)}>
                      {count} ({pct.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className={cn(
                        "rv-reporting-bar-grow h-full rounded-full bg-gradient-to-r",
                        meta.bar,
                      )}
                      style={{
                        width: `${Math.max(count > 0 ? 2 : 0, pct)}%`,
                        animationDelay: `${120 + index * 90}ms`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function UpsellBody({ isDarkMode }: { isDarkMode: boolean }) {
  const addon = getAddonCatalogEntry("qa-verifications");
  const features = [
    translateUi("Automated verification checks across safety-critical records"),
    translateUi("Printable and downloadable assurance reports for audits"),
    translateUi("Live compliance rate on Reporting & Analytics"),
    translateUi("Structured C / Advisory / Review findings with audit trail"),
  ];

  const text = isDarkMode ? "text-slate-200" : "text-slate-700";
  const heading = isDarkMode ? "text-white" : "text-slate-900";
  const hint = isDarkMode
    ? "border-indigo-500/35 bg-indigo-950/40 text-indigo-100"
    : "border-indigo-200 bg-indigo-50 text-indigo-900";
  const card = isDarkMode
    ? "border-indigo-500/30 bg-gradient-to-br from-indigo-950/70 via-violet-950/50 to-slate-900"
    : "border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50 to-white";

  return (
    <div className="space-y-4 p-4 sm:p-5">
      <div className={cn("rounded-2xl border p-4 sm:p-5", card)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ring-1",
              isDarkMode ? "bg-indigo-500/20 text-indigo-200 ring-indigo-400/30" : "bg-indigo-600 text-white ring-indigo-500/20",
            )}
          >
            <FileCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className={cn("text-lg font-bold", heading)}>{addon?.name ?? translateUi("QA Verifications")}</p>
            <p className={cn("mt-1 text-sm leading-relaxed", text)}>
              {addon?.shortDescription ??
                translateUi(
                  "Run automated employee verification checks and produce printable / downloadable compliance reports.",
                )}
            </p>
          </div>
        </div>
      </div>

      <p className={cn("text-sm leading-relaxed", text)}>
        {addon?.howItHelps ??
          translateUi(
            "Gives managers a structured way to evidence that safety-critical checks are being completed, with a clear audit trail for audits and assurance reviews.",
          )}
      </p>

      <ul className="grid gap-2 sm:grid-cols-2">
        {features.map((feature) => (
          <li key={feature} className={cn("flex items-start gap-2 text-sm", text)}>
            <CheckCircle2
              className={cn("mt-0.5 h-4 w-4 shrink-0", isDarkMode ? "text-indigo-300" : "text-indigo-600")}
              aria-hidden
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className={cn("rounded-xl border px-3 py-2.5 text-sm", hint)}>
        {translateUi("QA compliance rate appears here once the QA Verifications add-on is activated for your company.")}
      </div>
    </div>
  );
}

export function ReportingQaComplianceModal({
  open,
  mode,
  data,
  isDarkMode,
  isAdmin,
  onClose,
  onExploreAddons,
  onOpenQaVerifications,
}: {
  open: boolean;
  mode: "breakdown" | "upsell";
  data: ReportingQaComplianceData | null;
  isDarkMode: boolean;
  isAdmin?: boolean;
  onClose: () => void;
  onExploreAddons?: () => void;
  onOpenQaVerifications?: () => void;
}) {
  useUiLocale();
  const isBreakdown = mode === "breakdown";
  const qaAddonName = getAddonCatalogEntry("qa-verifications")?.name ?? translateUi("QA Verifications");

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const title =
    mode === "breakdown"
      ? translateUi("QA compliance rate")
      : translateUi("Unlock QA compliance insights");
  const subtitle =
    mode === "breakdown"
      ? translateUi("A transparent view of how your headline compliance rate is calculated.")
      : translateUi("See exactly why your workforce is compliant — and where attention is needed.");

  return (
    <div
      className="rv-reporting-modal-backdrop fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/70 p-0 backdrop-blur-md sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reporting-qa-modal-title"
        className={cn(
          "rv-reporting-modal-panel relative flex max-h-[min(92vh,720px)] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl border shadow-2xl sm:max-w-4xl sm:rounded-3xl",
          isBreakdown
            ? "border-slate-200 bg-white"
            : isDarkMode
              ? "border-slate-600 bg-slate-900 text-slate-100"
              : "border-slate-200 bg-white text-slate-900",
        )}
        onClick={(event) => event.stopPropagation()}
      >
        {!isBreakdown ? <GbrModalBackdrop opacity={isDarkMode ? 0.05 : 0.04} /> : null}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="border-b border-white/10 bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-900 px-4 py-3.5 sm:px-5 sm:py-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/25">
                {mode === "breakdown" ? <CheckCircle2 className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <h3 id="reporting-qa-modal-title" className="text-lg font-bold tracking-tight text-white">
                  {title}
                </h3>
                <p className="mt-0.5 text-sm leading-snug text-indigo-50/95">{subtitle}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-white/90 transition hover:bg-white/15"
                aria-label={translateUi("Close")}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {mode === "breakdown" && data ? (
              <BreakdownBody data={data} animate={open} />
            ) : mode === "breakdown" ? (
              <div className="bg-white p-3 sm:p-4">
                <div
                  className={cn(
                    DASHBOARD_GRADIENT_PANE_SHELL,
                    "flex min-h-[200px] items-center justify-center p-5 text-center",
                  )}
                >
                  <DashboardGradientPaneGlows />
                  <p className="relative text-sm text-slate-300">
                    {translateUi(
                      "No QA verification data is available yet. Run verifications for your employees to calculate a compliance rate.",
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <UpsellBody isDarkMode={isDarkMode} />
            )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5",
              isBreakdown ? "border-slate-200 bg-white" : isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white",
            )}
          >
            <p
              className={cn(
                "text-xs leading-snug sm:max-w-[min(100%,22rem)]",
                isBreakdown || !isDarkMode ? "text-slate-500" : "text-slate-400",
              )}
            >
              {translateUi("{{module}} add-on — provided by Rail Intel", { module: qaAddonName })}
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  "min-h-[44px] rounded-xl px-4 text-sm font-semibold",
                  isBreakdown ? "text-slate-700 hover:bg-slate-100" : isDarkMode ? "text-slate-300 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100",
                )}
              >
                {translateUi("Close")}
              </button>
              {mode === "upsell" && isAdmin && onExploreAddons ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onExploreAddons();
                  }}
                  className="min-h-[44px] rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500"
                >
                  {translateUi("Explore add-ons")}
                </button>
              ) : null}
              {mode === "breakdown" && onOpenQaVerifications ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenQaVerifications();
                  }}
                  className="min-h-[44px] rounded-xl bg-violet-600 px-5 text-sm font-semibold text-white shadow-lg shadow-violet-600/25 transition hover:bg-violet-500"
                >
                  {translateUi("Open QA Verifications")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
